/**
 * **JS 节点**（内核提供的通用逻辑节点）—— 用户可以带一段 JS、自己声明输入。
 *
 * 决策与契约见 `docs/js-node.md`。这里只说**落地形态**：
 *
 * ```
 *   输入端口      **用户声明**（`ports` 参数，JSON）：名字 / 类型 / 固定或变参
 *   输出端口      由 `code` 里的 `return` **推出来**（见 jsRuntime.deriveOutputPorts）
 *   代码          `code` 参数（一段函数体，`return { 名字: 值 }`）
 *   设计期        **不执行**（产出空值占位）
 *   运行期        `jsRuntime.runJsNode` —— 同步；要发请求请用副作用节点
 * ```
 *
 * ## 输出端口为什么不再手动配（2027 改，用户原话）
 *
 * > 我修改了代码的输出，从默认的一个对象改成了一个数组（里面有两个对象），
 * > 但是这个时候属性面板上没有多出来新的输出端口。去掉手动添加输出端口的
 * > 功能，通过检查代码的 return 来判断输出端口有多少个。
 *
 * 手动声明 + 代码 return 是**两份真相**，它们对不上时表现是"下游拿不到值"。
 * 现在只剩一份：**代码**。`return { a, b }` → 两个端口，`return [x, y]`
 * → 两个端口（0 / 1），看不出来就一个「结果」端口扛全部。
 *
 * ## 端口 handle 是**下标**（`out0` / `out1`），不是键名
 *
 * 键名只是**标签**。理由：改一次代码（加一个键、改一个名字）不该把已经
 * 连好的线全拆掉 —— 只要那一位还在，`out0` 就还在，线就还接着。
 *
 * ## 为什么 `ports` / `code` 是**参数行**而不是模型上的结构
 *
 * 参数存在的意义就是"随文档走、可序列化、可 diff" —— 端口的**声明**正是这种东西。
 * 存成参数之后：换台机器打开、导出模板、进 IR、给运行端看，全都是同一份。
 * 界面在哪儿编辑它（现在是「属性」面板）是**另一件事**（见 `PlaygroundProperties`）。
 *
 * ⚠️ 这两行是 `panelOnly`：**节点上不画**（用户原话："JS 代码节点不要显示代码，
 * 这个参数在节点上也不要显示端口的信息，就只显示有哪些输入输出端口就行"）。
 * 它们留在 `rows` 里是因为**值存在那儿**（面板按 rowId 读写）。
 */
import type { GraphNodeModel, NodeRow } from '../types/node'
import type { NodeTypeDef } from './package'
import type { NodeEvaluator } from './evaluate'
import { deriveOutputPorts, runJsNode, type JsPortDecl } from './jsRuntime'
import type { NodeRunner } from './runGraph'
import type { SocketType } from '../types/socket'

/** `ports` 参数里存的那份声明 —— **只有输入**（输出由代码推） */
export interface JsPortsConfig {
  inputs: JsPortDecl[]
}

/**
 * 解析 `ports` 参数（一串 JSON）。
 *
 * ⚠️ **解析失败不许抛**：这是用户手边的一个文本框（或者别人手改过的模板文件），
 * 打错了半个括号不该让整个节点炸掉（更不能让设计期报"求值失败"）——
 * 解析不出来就当"还没配置"，面板上给一句提示。容错在这里是**必须**的，不是偷懒。
 *
 * ⚠️ 老文档里可能还带着 `outputs` 那一份声明：**直接忽略**。它已经不参与
 * 任何判断（端口从代码推），留着只会在面板上显示一排用户改不动的假端口。
 */
export function parsePortsConfig(raw: unknown): JsPortsConfig {
  return tryParsePortsConfig(raw) ?? { inputs: [] }
}

/**
 * 严格一点的版本：**读不出来返回 `null`**（而不是"当成空的"）。
 *
 * 为什么要有这个区分：`rebuildJsRows` 会把行**按这份声明重建**。
 * 把"读不出来"当成"一个端口都没有"的话，一份坏掉的 JSON 会让节点的
 * 输入端口全部消失（顺手把连在上面的线变成悬空边）—— 那是**数据丢失**，
 * 而调用方只是想刷新一下行。所以那里用这个版本，读不出来就**原样不动**。
 */
export function tryParsePortsConfig(raw: unknown): JsPortsConfig | null {
  if (raw === undefined) return { inputs: [] }
  if (typeof raw !== 'string' || raw.trim() === '') return { inputs: [] }
  try {
    const parsed = JSON.parse(raw) as Partial<JsPortsConfig>
    const list = parsed.inputs
    if (list !== undefined && !Array.isArray(list)) return null
    return {
      inputs: Array.isArray(list)
        ? list
            .filter((item): item is JsPortDecl => Boolean(item) && typeof item === 'object')
            .map((item) => ({
              name: String(item.name ?? ''),
              type: (item.type ?? 'text') as SocketType,
              mode: item.mode === 'variadic' ? 'variadic' : 'fixed',
            }))
        : [],
    }
  } catch {
    return null
  }
}

/** 把那串 JSON 写回去（面板用；保持稳定顺序，diff 才好看） */
export function serializePortsConfig(config: JsPortsConfig): string {
  return JSON.stringify({ inputs: config.inputs }, null, 2)
}

/** 端口类型选项（界面上给用户选的） */
export const JS_PORT_TYPES: readonly SocketType[] = [
  'text',
  'number',
  'boolean',
  'color',
  'datetime',
  'list',
  'any',
]

/**
 * **新建时的默认输入声明**：两个固定输入。
 *
 * ⚠️ 节点的**行**由它生成（`rowsForJsNode`），不再手写 ——
 * 手写一份行 + 一份默认声明，两处一定会漂（改了端口数、行没跟着变）。
 */
export const DEFAULT_JS_PORTS: JsPortsConfig = {
  inputs: [
    { name: 'in0', type: 'any', mode: 'fixed' },
    { name: 'in1', type: 'any', mode: 'fixed' },
  ],
}

/**
 * 默认代码 —— 它同时是**输出的样板**：
 *
 * > 你 return 的那个对象有几个键，节点上就长出几个输出端口。
 */
export const DEFAULT_JS_CODE = [
  '// inputs.名字 读输入；return 的对象有几个键，节点上就长出几个输出端口',
  'return { out: inputs.in0 }',
].join('\n')

/**
 * **由输入声明 + 代码生成行**。
 *
 * ```
 *   固定输入  → 一路 `input` 行（handle = `in0` / `in1` …，按固定输入的序号）
 *   变参输入  → **一行变参行**（handle = `args.0` / `args.1` …；代码里是数组）
 *   输出      → 一路 `output` 行（handle = `out0` / `out1` …，**按位置编号**；
 *               标签是代码里那个键）
 *   面板专用  → 末尾两行 `ports` / `code`（`panelOnly`，节点上不画）
 * ```
 *
 * ⚠️ **输入编号只看"同类里的第几个"**：变参那一组不占固定输入的序号 ——
 * 否则在中间插一个变参行，后面所有固定输入的 `inK` 都会串位，
 * 已经连好的线就会接到别的变量上（这种 bug 在界面上看不出来，只会算错）。
 *
 * ⚠️ **只支持一组变参**（多组的话代码里两个同名数组没意义）。面板上切换时
 * 会把其它的关掉，这里也再做一次归一化。
 *
 * ⚠️ 变参行**只做输入**：输出侧不做变参端口 —— 输出是代码的 `return` 说了算，
 * 数组交给列表函数处理。
 */
export function rowsForJsNode(ports: JsPortsConfig, code: string): NodeRow[] {
  const rows: NodeRow[] = []
  let fixedInput = 0
  let variadicInput = 0
  for (const decl of ports.inputs) {
    if (decl.mode === 'variadic') {
      variadicInput += 1
      /* 只认第一组变参（再多也没处放） */
      if (variadicInput > 1) continue
      rows.push({
        kind: 'repeater',
        id: 'args',
        label: decl.name || '一组输入',
        socketType: (decl.type ?? 'any') as SocketType,
        min: 1,
        max: 8,
      })
      continue
    }
    rows.push({
      kind: 'input',
      id: `in${fixedInput}`,
      label: decl.name || `入 ${fixedInput}`,
      socket: { id: `in${fixedInput}`, type: (decl.type ?? 'any') as SocketType },
    })
    fixedInput += 1
  }

  /*
   * ★ 输出端口 = 代码里 `return` 的形状。名字只当标签，handle 按位置发。
   */
  deriveOutputPorts(code).forEach((port, index) => {
    rows.push({
      kind: 'output',
      id: `out${index}`,
      label: port.label,
      socket: { id: `out${index}`, type: 'any' },
    })
  })

  rows.push({
    kind: 'property',
    id: 'ports',
    label: '端口',
    panelOnly: true,
    widget: { id: 'ports', kind: 'ports', value: serializePortsConfig(ports) },
  })
  rows.push({
    kind: 'property',
    id: 'code',
    label: '代码',
    panelOnly: true,
    /*
     * ⚠️ 代码要**原样放进去**（不是默认那段）：重建行的时候把用户写的代码
     * 换成默认值，就是"我改的代码被换回默认"那类 bug 的一半原因。
     */
    widget: { id: 'code', kind: 'code', value: code },
  })
  return rows
}

/** 从一行里读出控件值（`ports` / `code` 都存在控件值里） */
function widgetValueOf(model: GraphNodeModel, rowId: string): unknown {
  const row = model.rows.find((item) => item.id === rowId)
  return row && (row.kind === 'property' || row.kind === 'input') ? row.widget?.value : undefined
}

/**
 * **按模型当前的 `ports` / `code` 值重建行**（纯函数）。
 *
 * 调用方负责善后（宿主那边要顺手删掉"已经不存在端口"上的边，见
 * `Playground.rebuildJsNodeRows`）—— 这里只做"值 → 行"这一件事，
 * 所以打开文档、改代码、改端口三条路能共用同一份重建逻辑。
 *
 * ⚠️ **端口声明读不出来时原样返回旧行**（不重建）：把"读不出来"当成
 * "一个输入都没有"的话，一份坏掉的 JSON 会把节点的输入端口全部抹掉，
 * 连带把线上的边变成悬空边 —— 那是在别人只想刷新一下行的时候**丢数据**。
 */
export function rebuildJsRows(model: GraphNodeModel): NodeRow[] {
  const raw = widgetValueOf(model, 'ports')
  const ports = tryParsePortsConfig(raw)
  if (ports === null) return model.rows
  const code = widgetValueOf(model, 'code')
  return rowsForJsNode(ports, code === undefined ? DEFAULT_JS_CODE : String(code))
}

/**
 * **打开文档时归一化一个 JS 节点**：行按它自己的 `ports` / `code` 重建。
 *
 * 为什么需要：行是**存在文档里的**（`rows` 是功能数据）。老文档里的行是
 * 按"手动声明输出端口"那一版存下来的 —— 直接装进来的话，用户会看到
 * 一排**和代码对不上**的输出端口，而且 `code` / `ports` 两行还画在节点上
 * （用户这次明确要求不要）。
 *
 * ⚠️ 这里**不动边**：宿主重建行时会顺手删掉悬空边（`danglingEdgesAfter`），
 * 而打开文档这条路上边是文档自己带来的 —— 该不该删要在有完整图上下文
 * 的地方判断，不该在一个"归一化模型"的纯函数里悄悄做。
 */
export function normalizeJsNodeRows(model: GraphNodeModel): GraphNodeModel {
  if (model.type !== JS_NODE_TYPE.type) return model
  model.rows = rebuildJsRows(model)
  return model
}

/** 一条边的最小形状（只用到这四个字段 —— 免得为了一个纯函数去依赖图引擎的类型） */
export interface EdgeLike {
  id: string
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

/**
 * **重建行之后，哪些边成了悬空的**（要删掉）。
 *
 * 为什么必须有这一步：声明里删掉一路端口，那条线在图上就接在了
 * **不存在的端口**上 —— 画面上是一条接在空气里的线，校验会说"找不到端口"。
 * 而它是**用户自己刚删的**，所以删线是符合预期的（不是悄悄丢数据：
 * 删端口这个动作本身就该问"要不要连它的线" —— 那是下一步的交互，
 * 现在至少不能留下悬空的线）。
 *
 * ⚠️ 抽成纯函数是为了能被单测覆盖：它原来写在宿主的重建流程里，
 * 而宿主那段没有断言（打进去一个"不清理"的变异，套件照样绿 —— 我验过）。
 */
export function danglingEdgesAfter(edges: readonly EdgeLike[], nodeId: string, handles: ReadonlySet<string>): string[] {
  return edges
    .filter((edge) => {
      if (edge.source === nodeId && edge.sourceHandle && !handles.has(edge.sourceHandle)) return true
      if (edge.target === nodeId && edge.targetHandle && !handles.has(edge.targetHandle)) return true
      return false
    })
    .map((edge) => edge.id)
}

/** 一组行里所有的端口 handle（含变参实例） */
export function handlesOfRows(rows: readonly NodeRow[]): Set<string> {
  const handles = new Set<string>()
  for (const row of rows) {
    if (row.kind === 'input' || row.kind === 'output') handles.add(row.socket.id)
    if (row.kind === 'repeater') {
      for (let index = 0; index < (row.max ?? 1); index += 1) {
        handles.add((row.handlePattern ?? `${row.id}.{i}`).replace('{i}', String(index)))
      }
    }
  }
  return handles
}

export const JS_NODE_TYPE: NodeTypeDef = {
  type: 'core.js',
  title: 'JS · 代码',
  category: 'utility',
  width: 232,
  keywords: ['js', 'javascript', 'code', 'script', '代码', '脚本', '函数'],
  hint: '写一段 JS：读 inputs.名字，return 一个对象（有几个键就有几个输出端口；只在运行时执行）',
  /* 大括号 + 一个斜杠：一眼能认出"这是写代码的" */
  icon: '<path d="M8 6 L3.5 12 L8 18"/><path d="M16 6 L20.5 12 L16 18"/>',
  /*
   * ★ **代码在属性面板里**（节点上不显示代码，见本文件头）。于是这个按钮
   * 是"去看代码"的唯一入口 —— 用户 2027 要的就是它：
   *
   * > js 代码节点，可以给增加一个按钮（静音按钮旁，跳转打开属性面板）
   *
   * ⚠️ 走的是**通用机制**（`NodeTypeDef.actions` + 宿主的 `NODE_ACTIONS`），
   * 不是"给 JS 节点特判一个按钮"—— 别的类型想加就是加一行声明的事。
   */
  actions: [
    {
      id: 'settings',
      icon: '<path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/>',
      label: '打开属性面板改代码（也可以双击节点标题下面的设置行）',
    },
  ],
  /* ★ 行由**默认声明 + 默认代码**生成 —— 端口数是它们说了算 */
  rows: rowsForJsNode(DEFAULT_JS_PORTS, DEFAULT_JS_CODE),
}

/**
 * 设计期：**空值**。
 *
 * ⚠️ 这是"只在 Run 时执行"的落点：设计期每改一个字符都跑一次用户代码
 * （还可能发请求 / 改数据）是不可能的。空对象 = "算过了但没有值"，
 * 下游因此回退到自己的内联默认值。
 */
export const jsDesignTime: NodeEvaluator = () => ({})

/** 运行期：把声明出来的输入绑成变量，跑代码，再按代码推出来的端口摊开 */
export const jsRunner: NodeRunner = async (api) => {
  const ports = parsePortsConfig(api.literal('ports'))
  const code = String(api.literal('code') ?? '')

  /* 按声明从图上取输入：固定参数走 `inK`，变参那一组走变参行 */
  const values: Record<string, unknown> = {}
  let fixedIndex = 0
  for (const decl of ports.inputs) {
    if (decl.mode === 'variadic') {
      values[decl.name] = api.repeat('args')
      continue
    }
    values[decl.name] = api.input(`in${fixedIndex}`)
    fixedIndex += 1
  }

  const result = runJsNode({ inputs: ports.inputs, code }, values)
  if (!result.ok) {
    /*
     * ⚠️ 诊断要**指到名字**上（"哪个输出没返回"），而不是一句"运行失败" ——
     * 这类错在图上表现为"下游拿不到值"，查起来很绕。
     */
    const first = result.problems[0]
    throw new Error(
      first
        ? `${first.name ? `「${first.name}」：` : ''}${first.message}`
        : '代码执行失败',
    )
  }

  /*
   * 按**位置**摊回端口（`out0` ↔ 第 0 个端口）：handle 是位置，
   * 键名只是标签 —— 两边用的是同一个 `deriveOutputPorts(code)`，不会错位。
   */
  const out: Record<string, unknown> = {}
  result.values.forEach((value, index) => {
    out[`out${index}`] = value
  })
  return out
}
